import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { BsChevronDown, BsX } from "react-icons/bs";
import profileimg from "../assets/profile.png";
import { getUser } from "../utils/auth";
import { authHeaders } from "./becomesell/LiveShoppingParts";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 10;
const GUEST_EVENTS = ["guest-request", "guest-approved", "guest-rejected", "guest-removed", "guest-left", "guest-joined"];

// Defaults of the LiveStreams columns, sent with the configuration when going live
export const DEFAULT_LIVE_SETTINGS = {
  view_audience: "public",
  min_viewer_age: null,
  comment_audience: "everyone",
  comment_min_age: null,
  gifts_enabled: true,
  gift_audience: "everyone",
  min_gift_coins: null,
  guests_enabled: false,
  guest_approval_required: true,
  layout: "single",
};

export const MODERATOR_PERMISSIONS = [
  { key: "can_block_users", label: "Block/remove users" },
  { key: "can_remove_comments", label: "Remove comments" },
  { key: "can_pin_comments", label: "Pin comments" },
  { key: "can_highlight_comments", label: "Highlight comments" },
];

export const HOST_PERMISSIONS = Object.fromEntries(MODERATOR_PERMISSIONS.map(({ key }) => [key, true]));

const VIEW_AUDIENCES = [
  { value: "public", label: "Public" },
  { value: "followers", label: "Followers" },
  { value: "friends", label: "Friends" },
];

const INTERACTION_AUDIENCES = [
  { value: "everyone", label: "Everyone" },
  { value: "followers", label: "Followers" },
  { value: "friends", label: "Friends" },
];

const VIEWER_AGES = [
  { value: null, label: "None" },
  { value: 15, label: "15+" },
  { value: 18, label: "18+" },
];

const COMMENT_MIN_AGE = 18;

// Wider audiences rank lower: friends (mutual followers) are a subset of followers
const AUDIENCE_RANK = { public: 0, everyone: 0, followers: 1, friends: 2 };

// Only people who can watch can comment or send gifts, so those audiences are narrowed with the viewers
const narrowToViewers = (settings, patch) => {
  if (!patch.view_audience) return patch;
  const rank = AUDIENCE_RANK[patch.view_audience];
  const narrow = (key) => (AUDIENCE_RANK[settings[key]] < rank ? { [key]: patch.view_audience } : {});
  return { ...patch, ...narrow("comment_audience"), ...narrow("gift_audience") };
};

const interactionOptions = (viewAudience) =>
  INTERACTION_AUDIENCES.map((option) => ({ ...option, disabled: AUDIENCE_RANK[option.value] < AUDIENCE_RANK[viewAudience] }));

const errorMessage = (error, fallback) => error.response?.data?.message || error.message || fallback;

export const displayName = (person) =>
  [person?.first_name, person?.last_name].filter(Boolean).join(" ") || person?.username || "User";

/**
 * Stream setup of the host studio. Before the live, moderators and co-hosts are kept locally and
 * added once the stream exists (the API only accepts them for a started stream); settings go with
 * the configuration. During the live every change is saved straight away.
 */
export const useLiveStudioSetup = (streamId, socket) => {
  const [settings, setSettings] = useState(DEFAULT_LIVE_SETTINGS);
  const [moderators, setModerators] = useState([]);
  const [guests, setGuests] = useState([]);
  const pendingRef = useRef({ moderators: [], cohosts: [] });

  // Only updated before the live: effects run in order, so this must not clear the list the effect below applies
  useEffect(() => {
    if (streamId) return;
    pendingRef.current = {
      moderators,
      cohosts: settings.guests_enabled ? guests.filter((g) => g.role === "cohost" && g.status !== "requested") : [],
    };
  }, [streamId, moderators, guests, settings.guests_enabled]);

  const loadModerators = useCallback(async () => {
    const res = await axios.get(`${API_BASE_URL}/live/${streamId}/moderators`, { headers: authHeaders() });
    setModerators(
      (res.data.result?.moderators || [])
        .filter((m) => m.moderator)
        .map((m) => ({ user: m.moderator, permissions: m.permissions }))
    );
  }, [streamId]);

  const loadGuests = useCallback(async () => {
    const res = await axios.get(`${API_BASE_URL}/live/${streamId}/guests`, { headers: authHeaders() });
    setGuests(res.data.result?.guests || []);
  }, [streamId]);

  useEffect(() => {
    if (!streamId) return;
    const { moderators: pendingModerators, cohosts: pendingCohosts } = pendingRef.current;
    const apply = async () => {
      const results = await Promise.allSettled([
        ...pendingModerators.map((m) =>
          axios.post(
            `${API_BASE_URL}/live/${streamId}/moderators`,
            { moderator_id: m.user.id, permissions: m.permissions },
            { headers: authHeaders() }
          )
        ),
        ...pendingCohosts.map((g) =>
          axios.post(
            `${API_BASE_URL}/live/${streamId}/guests/invite`,
            { user_id: g.user.id, role: "cohost", pre_approved: true },
            { headers: authHeaders() }
          )
        ),
      ]);
      results
        .filter((r) => r.status === "rejected")
        .forEach((r) => toast.error(errorMessage(r.reason, "Unable to add a moderator or host")));
      await Promise.all([
        loadModerators(),
        loadGuests(),
        // A live resumed after a reload starts from the saved settings, not the defaults
        axios.get(`${API_BASE_URL}/live/configure/${streamId}`, { headers: authHeaders() }).then((res) =>
          setSettings((prev) =>
            Object.fromEntries(Object.keys(prev).map((key) => [key, res.data[key] === undefined ? prev[key] : res.data[key]]))
          )
        ),
      ]).catch(() => {});
    };
    apply();
  }, [streamId, loadModerators, loadGuests]);

  useEffect(() => {
    if (!socket || !streamId) return;
    const userId = getUser()?.id;
    const reload = () => loadGuests().catch(() => {});
    // Guest requests reach the host through the personal room joined with the engagement room
    const joinRoom = () => socket.emit("join-stream-engagement", { streamId, userId });
    joinRoom();
    socket.on("connect", joinRoom);
    GUEST_EVENTS.forEach((event) => socket.on(event, reload));
    return () => {
      socket.emit("leave-stream-engagement", { streamId, userId });
      socket.off("connect", joinRoom);
      GUEST_EVENTS.forEach((event) => socket.off(event, reload));
    };
  }, [socket, streamId, loadGuests]);

  const updateSettings = async (patch) => {
    const changes = narrowToViewers(settings, patch);
    if (streamId) {
      try {
        await axios.patch(`${API_BASE_URL}/live/configure/${streamId}`, changes, { headers: authHeaders() });
      } catch (error) {
        toast.error(errorMessage(error, "Unable to update the live settings"));
        return;
      }
    }
    setSettings((prev) => ({ ...prev, ...changes }));
  };

  const addModerator = async (person) => {
    const permissions = { ...HOST_PERMISSIONS };
    if (streamId) {
      try {
        await axios.post(
          `${API_BASE_URL}/live/${streamId}/moderators`,
          { moderator_id: person.id, permissions },
          { headers: authHeaders() }
        );
      } catch (error) {
        toast.error(errorMessage(error, "Unable to add the moderator"));
        return;
      }
    }
    setModerators((prev) => [...prev, { user: person, permissions }]);
  };

  const updateModeratorPermission = async (moderatorId, key, value) => {
    const moderator = moderators.find((m) => m.user.id === moderatorId);
    const permissions = { ...moderator.permissions, [key]: value };
    if (streamId) {
      try {
        await axios.put(
          `${API_BASE_URL}/live/${streamId}/moderators/${moderatorId}`,
          { permissions },
          { headers: authHeaders() }
        );
      } catch (error) {
        toast.error(errorMessage(error, "Unable to update the permissions"));
        return;
      }
    }
    setModerators((prev) => prev.map((m) => (m.user.id === moderatorId ? { ...m, permissions } : m)));
  };

  const removeModerator = async (moderatorId) => {
    if (streamId) {
      try {
        await axios.delete(`${API_BASE_URL}/live/${streamId}/moderators/${moderatorId}`, { headers: authHeaders() });
      } catch (error) {
        toast.error(errorMessage(error, "Unable to remove the moderator"));
        return;
      }
    }
    setModerators((prev) => prev.filter((m) => m.user.id !== moderatorId));
  };

  const addCohost = async (person) => {
    if (!streamId) {
      setGuests((prev) => [...prev, { user_id: person.id, user: person, role: "cohost", status: "approved" }]);
      return;
    }
    try {
      await axios.post(
        `${API_BASE_URL}/live/${streamId}/guests/invite`,
        { user_id: person.id, role: "cohost", pre_approved: true },
        { headers: authHeaders() }
      );
      await loadGuests();
    } catch (error) {
      toast.error(errorMessage(error, "Unable to add the host"));
    }
  };

  const removeGuest = async (userId) => {
    if (!streamId) {
      setGuests((prev) => prev.filter((g) => g.user_id !== userId));
      return;
    }
    try {
      await axios.delete(`${API_BASE_URL}/live/${streamId}/guests/${userId}`, { headers: authHeaders() });
      await loadGuests();
    } catch (error) {
      toast.error(errorMessage(error, "Unable to remove the guest"));
    }
  };

  const reviewRequest = async (userId, action) => {
    try {
      await axios.post(`${API_BASE_URL}/live/${streamId}/guests/${userId}/review`, { action }, { headers: authHeaders() });
      await loadGuests();
    } catch (error) {
      toast.error(errorMessage(error, "Unable to update the request"));
    }
  };

  return {
    settings,
    updateSettings,
    moderators,
    addModerator,
    updateModeratorPermission,
    removeModerator,
    cohosts: guests.filter((g) => g.role === "cohost" && g.status !== "requested"),
    guests: guests.filter((g) => g.role !== "cohost" && g.status !== "requested"),
    requests: guests.filter((g) => g.status === "requested"),
    addCohost,
    removeGuest,
    reviewRequest,
  };
};

export const CollapsibleSection = ({ title, note, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="rounded-xl border border-gray-200 bg-white">
      <button onClick={() => setOpen((value) => !value)} className="flex w-full items-center justify-between px-4 py-3">
        <span className="font-semibold text-[#095228]">
          {title}
          {note && <span className="ml-2 text-[10px] font-normal text-[#BE1F2E]">{note}</span>}
        </span>
        <BsChevronDown className={`transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="space-y-4 border-t px-4 py-4">{children}</div>}
    </section>
  );
};

const RadioGroup = ({ label, options, value, onChange, disabled }) => (
  <div>
    <p className="mb-2 text-sm font-medium">{label}</p>
    <div className="flex flex-wrap gap-4">
      {options.map((option) => (
        <label
          key={String(option.value)}
          className={`flex items-center gap-2 text-sm ${disabled || option.disabled ? "text-gray-400" : ""}`}
        >
          <input
            type="radio"
            checked={value === option.value}
            onChange={() => onChange(option.value)}
            disabled={disabled || option.disabled}
            className="accent-[#095228]"
          />
          {option.label}
        </label>
      ))}
    </div>
  </div>
);

const CheckboxField = ({ label, checked, onChange, disabled }) => (
  <label className="flex items-center gap-2 text-sm">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
      className="accent-[#095228]"
    />
    {label}
  </label>
);

const PersonRow = ({ person, children }) => (
  <div className="flex items-center gap-3">
    <img src={person.profile_picture_url || profileimg} alt="" className="h-9 w-9 rounded-full object-cover" />
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-semibold">{displayName(person)}</p>
      {person.username && <p className="truncate text-xs text-gray-500">@{person.username}</p>}
    </div>
    {children}
  </div>
);

// Search among the host's followers (friends are mutual followers, so they are included)
const FollowerSearch = ({ placeholder, excludeIds, onSelect }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const term = query.trim().toLowerCase();
    if (!term) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.post(
          `${API_BASE_URL}/friend/get-friends`,
          { filters: { allFollowers: true, search: term }, page: 1, limit: SEARCH_LIMIT },
          { headers: authHeaders() }
        );
        // The API also matches on the host's own name, so the term is checked on the follower too
        const people = (res.data.result?.friends || [])
          .map((friend) => friend.User1)
          .filter(
            (person) =>
              person &&
              [person.username, person.first_name, person.last_name, displayName(person)].some((value) =>
                String(value || "").toLowerCase().includes(term)
              )
          );
        if (!cancelled) setResults(people);
      } catch (error) {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  const visible = results.filter((person) => !excludeIds.includes(person.id));

  return (
    <div className="relative">
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        maxLength={100}
        placeholder={placeholder}
        className="w-full rounded-full border border-gray-300 px-4 py-2 text-sm focus:outline-none"
      />
      {query.trim() && (
        <div className="absolute left-0 right-0 z-20 mt-1 max-h-60 space-y-3 overflow-y-auto rounded-xl border bg-white p-3 shadow-lg">
          {loading && <p className="text-xs text-gray-400">Searching...</p>}
          {!loading && visible.length === 0 && <p className="text-xs text-gray-400">No followers found</p>}
          {visible.map((person) => (
            <PersonRow key={person.id} person={person}>
              <button
                onClick={() => {
                  onSelect(person);
                  setQuery("");
                }}
                className="rounded-full bg-[#095228] px-4 py-1 text-xs font-semibold text-white"
              >
                Add
              </button>
            </PersonRow>
          ))}
        </div>
      )}
    </div>
  );
};

const RemoveButton = ({ onClick, title }) => (
  <button onClick={onClick} title={title} className="rounded-full p-1 text-gray-500 hover:bg-gray-100">
    <BsX className="text-lg" />
  </button>
);

export const ModeratorsSection = ({ moderators, excludeIds, onAdd, onPermissionChange, onRemove }) => (
  <CollapsibleSection title="Moderation - Live Moderators">
    <FollowerSearch placeholder="Search Followers / Friends" excludeIds={excludeIds} onSelect={onAdd} />
    {moderators.map(({ user, permissions }) => (
      <div key={user.id} className="space-y-2 rounded-xl bg-[#F9FAFB] p-3">
        <PersonRow person={user}>
          <RemoveButton onClick={() => onRemove(user.id)} title="Remove moderator" />
        </PersonRow>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {MODERATOR_PERMISSIONS.map(({ key, label }) => (
            <CheckboxField
              key={key}
              label={label}
              checked={!!permissions?.[key]}
              onChange={(value) => onPermissionChange(user.id, key, value)}
            />
          ))}
        </div>
      </div>
    ))}
  </CollapsibleSection>
);

export const MultiHostSection = ({ cohosts, excludeIds, onAdd, onRemove }) => (
  <CollapsibleSection title="Allow Multi-Host">
    <FollowerSearch placeholder="Add Host" excludeIds={excludeIds} onSelect={onAdd} />
    {cohosts.map((guest) => (
      <div key={guest.user_id} className="rounded-xl bg-[#F9FAFB] p-3">
        <PersonRow person={guest.user || { id: guest.user_id }}>
          {guest.status === "invited" && <span className="text-xs text-gray-500">Invited</span>}
          <RemoveButton onClick={() => onRemove(guest.user_id)} title="Remove host" />
        </PersonRow>
      </div>
    ))}
  </CollapsibleSection>
);

export const PrivacyControls = ({ settings, onChange }) => (
  <CollapsibleSection title="Privacy Controls">
    {/* A private live is switched back to public from the visibility selector */}
    <RadioGroup
      label="Who Can View"
      options={VIEW_AUDIENCES.map((option) => ({
        ...option,
        disabled: option.value === "public" && settings.view_audience !== "public",
      }))}
      value={settings.view_audience}
      onChange={(value) => onChange({ view_audience: value })}
    />
    <RadioGroup
      label="Age Restriction"
      options={VIEWER_AGES}
      value={settings.min_viewer_age}
      onChange={(value) => onChange({ min_viewer_age: value })}
    />
    <RadioGroup
      label="Who Can Comment"
      options={interactionOptions(settings.view_audience)}
      value={settings.comment_audience}
      onChange={(value) => onChange({ comment_audience: value })}
    />
    <CheckboxField
      label={`Allow comments only from ${COMMENT_MIN_AGE}+ users`}
      checked={settings.comment_min_age === COMMENT_MIN_AGE}
      onChange={(checked) => onChange({ comment_min_age: checked ? COMMENT_MIN_AGE : null })}
    />
  </CollapsibleSection>
);

export const GuestPrivacy = ({ settings, onChange }) => (
  <CollapsibleSection title="Guest & Multi-Host Privacy">
    <CheckboxField
      label="Allow Guests & Multi-Host"
      checked={settings.guests_enabled}
      onChange={(checked) => onChange({ guests_enabled: checked })}
    />
    {settings.guests_enabled && (
      <RadioGroup
        label="Guest Requests"
        options={[
          { value: false, label: "Auto-Approve" },
          { value: true, label: "Manual Approval" },
        ]}
        value={settings.guest_approval_required}
        onChange={(value) => onChange({ guest_approval_required: value })}
      />
    )}
  </CollapsibleSection>
);

export const GiftingControls = ({ settings, onChange, live }) => {
  const [minCoins, setMinCoins] = useState(settings.min_gift_coins ?? "");

  useEffect(() => {
    setMinCoins(settings.min_gift_coins ?? "");
  }, [settings.min_gift_coins]);

  const commitMinCoins = () => {
    const value = minCoins === "" ? null : Number(minCoins);
    if (value !== null && (!Number.isFinite(value) || value < 0)) {
      toast.error("Minimum gift must be 0 or more coins");
      setMinCoins(settings.min_gift_coins ?? "");
      return;
    }
    const rounded = value === null ? null : Math.round(value * 100) / 100;
    if (rounded !== settings.min_gift_coins) onChange({ min_gift_coins: rounded });
  };

  return (
    <CollapsibleSection title="Gifting Controls" note={live ? "*Edit Allowed" : null}>
      <CheckboxField
        label="Enable Virtual Gifts"
        checked={settings.gifts_enabled}
        onChange={(checked) => onChange({ gifts_enabled: checked })}
      />
      {settings.gifts_enabled && (
        <>
          <RadioGroup
            label="Who Can Send Gifts"
            options={interactionOptions(settings.view_audience)}
            value={settings.gift_audience}
            onChange={(value) => onChange({ gift_audience: value })}
          />
          <div>
            <p className="mb-2 text-sm font-medium">Minimum Gift (coins)</p>
            <input
              type="number"
              min="0"
              step="0.01"
              value={minCoins}
              onChange={(e) => setMinCoins(e.target.value)}
              onBlur={commitMinCoins}
              onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
              placeholder="No minimum"
              className="w-40 rounded-full border border-gray-300 px-4 py-2 text-sm focus:outline-none"
            />
          </div>
          <p className="text-[10px] text-gray-500">*Gift earnings are shared equally between the host and joined co-hosts.</p>
        </>
      )}
    </CollapsibleSection>
  );
};

export const GuestRequests = ({ requests, guests, onReview, onRemove }) => {
  if (!requests.length && !guests.length) return null;
  return (
    <div className="space-y-3 rounded-xl bg-[#F9FAFB] p-4">
      <h3 className="font-semibold">Guest & Multi-Host Requests</h3>
      {requests.map((request) => (
        <PersonRow key={request.user_id} person={request.user || { id: request.user_id }}>
          <button
            onClick={() => onReview(request.user_id, "approve")}
            className="rounded-full bg-[#095228] px-3 py-1 text-xs font-semibold text-white"
          >
            Accept
          </button>
          <button
            onClick={() => onReview(request.user_id, "reject")}
            className="rounded-full border border-[#BE1F2E] px-3 py-1 text-xs font-semibold text-[#BE1F2E]"
          >
            Reject
          </button>
        </PersonRow>
      ))}
      {guests.map((guest) => (
        <PersonRow key={guest.user_id} person={guest.user || { id: guest.user_id }}>
          <span className="text-xs text-gray-500">{guest.status === "invited" ? "Invited" : "Approved"}</span>
          <RemoveButton onClick={() => onRemove(guest.user_id)} title="Remove guest" />
        </PersonRow>
      ))}
    </div>
  );
};

/**
 * Comment moderation for the host (every action) or a moderator (the permissions the host gave).
 * Returns the menu actions allowed on a comment; donations shown in the chat have no author id and get none.
 */
export const useCommentModeration = (streamId, permissions, hostId) => {
  const run = useCallback(
    async (request, success) => {
      try {
        await request();
        toast.success(success);
      } catch (error) {
        toast.error(errorMessage(error, "Unable to moderate the comment"));
      }
    },
    []
  );

  return useCallback(
    (comment) => {
      if (!streamId || !permissions || !comment.userId) return [];
      const me = Number(getUser()?.id);
      const authorId = Number(comment.userId);
      const url = `${API_BASE_URL}/live/${streamId}`;
      const manage = (action, success) =>
        run(() => axios.post(`${url}/comments/${comment.commentId}/manage`, { action }, { headers: authHeaders() }), success);
      const actions = [];

      if (permissions.can_block_users && authorId !== me && authorId !== Number(hostId)) {
        actions.push({
          label: "Block",
          onClick: () =>
            run(
              () => axios.post(`${url}/block-user`, { blocked_user_id: authorId }, { headers: authHeaders() }),
              "User blocked from this live"
            ),
        });
      }
      if (permissions.can_remove_comments) {
        actions.push({
          label: "Remove",
          onClick: () =>
            run(
              () => axios.post(`${url}/comments/${comment.commentId}/remove`, {}, { headers: authHeaders() }),
              "Comment removed"
            ),
        });
      }
      if (permissions.can_pin_comments) {
        actions.push(
          comment.isPinned
            ? { label: "Unpin", onClick: () => manage("unpin", "Comment unpinned") }
            : { label: "Pin", onClick: () => manage("pin", "Comment pinned") }
        );
      }
      if (permissions.can_highlight_comments) {
        actions.push(
          comment.isHighlighted
            ? { label: "Unhighlight", onClick: () => manage("unhighlight", "Highlight removed") }
            : { label: "Highlight", onClick: () => manage("highlight", "Comment highlighted") }
        );
      }
      return actions;
    },
    [streamId, permissions, hostId, run]
  );
};

// Likes on the live: the count from the API plus the realtime updates
export const useLiveLikes = (streamId, socket) => {
  const [likes, setLikes] = useState(0);

  useEffect(() => {
    if (!streamId) {
      setLikes(0);
      return;
    }
    axios
      .get(`${API_BASE_URL}/live/${streamId}/engagement`, { headers: authHeaders() })
      .then((res) => setLikes(res.data.result?.engagement?.likeCount || 0))
      .catch(() => {});
  }, [streamId]);

  useEffect(() => {
    if (!socket) return;
    const onReaction = (data) => setLikes(data.counts?.likeCount || 0);
    socket.on("reaction-updated", onReaction);
    return () => socket.off("reaction-updated", onReaction);
  }, [socket]);

  const like = async () => {
    try {
      const res = await axios.post(`${API_BASE_URL}/live/${streamId}/reaction`, { type: "like" }, { headers: authHeaders() });
      const count = res.data.result?.counts?.likeCount;
      if (count !== undefined) setLikes(count);
    } catch (error) {
      toast.error(errorMessage(error, "Unable to like the live"));
    }
  };

  return { likes, like };
};

/**
 * A viewer's seat on the stage of a live: null or the guest status "requested", "invited" or "approved". Guest events reach the
 * viewer through its personal room (useLiveViewer joins it); guests_enabled and layout follow the live settings.
 */
export const useGuestSeat = (streamId, stream, socket) => {
  const [seat, setSeat] = useState(null);
  const [settings, setSettings] = useState({ guests_enabled: false, layout: "single" });

  const loadSeat = useCallback(async () => {
    const me = Number(getUser()?.id);
    const res = await axios.get(`${API_BASE_URL}/live/${streamId}/guests`, { headers: authHeaders() });
    // The list includes the viewer's own open request/invitation, whose status is the seat
    setSeat((res.data.result?.guests || []).find((g) => Number(g.user_id) === me)?.status ?? null);
  }, [streamId]);

  useEffect(() => {
    if (!stream) return;
    setSettings({ guests_enabled: !!stream.guests_enabled, layout: stream.layout || "single" });
    if (stream.guests_enabled && ["live", "paused"].includes(stream.status)) loadSeat().catch(() => {});
  }, [stream, loadSeat]);

  useEffect(() => {
    if (!socket) return;
    const me = Number(getUser()?.id);
    const seats = { "guest-invite": "invited", "guest-approved": "approved" };
    const onGuestEvent = (event) => (data) => {
      if (Number(data.guest?.user_id) !== me) return;
      setSeat(seats[event] ?? (event === "guest-request" ? "requested" : null));
    };
    const handlers = ["guest-request", "guest-invite", "guest-approved", "guest-rejected", "guest-removed", "guest-left"].map(
      (event) => [event, onGuestEvent(event)]
    );
    const onSettings = (data) =>
      setSettings((prev) => ({
        guests_enabled: data.guests_enabled ?? prev.guests_enabled,
        layout: data.layout ?? prev.layout,
      }));
    handlers.forEach(([event, handler]) => socket.on(event, handler));
    socket.on("live-settings-updated", onSettings);
    socket.on("layout-changed", onSettings);
    return () => {
      handlers.forEach(([event, handler]) => socket.off(event, handler));
      socket.off("live-settings-updated", onSettings);
      socket.off("layout-changed", onSettings);
    };
  }, [socket]);

  const run = async (request, nextSeat) => {
    try {
      const res = await request();
      setSeat(nextSeat(res.data.result));
    } catch (error) {
      // Already requested or approved: the guest list tells which
      if (error.response?.status === 409) {
        await loadSeat().catch(() => setSeat("requested"));
        return;
      }
      toast.error(errorMessage(error, "Unable to update your request"));
    }
  };

  const requestToJoin = () =>
    run(
      () => axios.post(`${API_BASE_URL}/live/${streamId}/guests/request`, {}, { headers: authHeaders() }),
      (guest) => (guest?.status === "approved" ? "approved" : "requested")
    );

  const respondToInvite = (action) =>
    run(
      () => axios.post(`${API_BASE_URL}/live/${streamId}/guests/invitation/respond`, { action }, { headers: authHeaders() }),
      (guest) => (guest?.status === "approved" ? "approved" : null)
    );

  const leaveStage = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/live/${streamId}/guests/${getUser()?.id}`, { headers: authHeaders() });
    } catch (error) {
      if (error.response?.status !== 404) toast.error(errorMessage(error, "Unable to leave the stage"));
    }
    setSeat(null);
  };

  return { seat, settings, requestToJoin, respondToInvite, leaveStage };
};

